import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { EmployeeWorkload, GroupWorkload } from '../tasks.types';
import { fmtHours, fmtPct, num } from '../tasks.utils';
import { WorkloadChip } from './Chips';

const BAR = { UNDERUTILIZED: 'info.main', NORMAL: 'success.main', HIGH: 'warning.main', OVERLOADED: 'error.main', NO_CAPACITY: 'grey.400' };

/** Utilization as a number with a bar (capped at 150% so an overload still fits). */
function UtilBar({ value, status }: { value: EmployeeWorkload['utilization']; status: EmployeeWorkload['workloadStatus'] }) {
  const pct = Math.min(num(value), 150) / 1.5;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
      <Box sx={{ width: 70, height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: BAR[status] }} />
      </Box>
      <Box sx={{ minWidth: 48, textAlign: 'right' }}>{fmtPct(value)}</Box>
    </Box>
  );
}

type Row = Pick<EmployeeWorkload, 'capacityHours' | 'allocatedHours' | 'actualHours' | 'remainingHours' | 'utilization' | 'workloadStatus' | 'activeAllocationHours' | 'activeTasks' | 'overdueTasks'> & {
  key: string | number;
  name: string;
  sub?: string | null;
};

export const fromEmployee = (e: EmployeeWorkload): Row => ({ ...e, key: e.employeeId, name: e.employeeName, sub: e.departmentName });
export const fromGroup = (g: GroupWorkload): Row => ({ ...g, key: g.id ?? g.name, name: g.name, sub: `${g.employeeCount} people · ${g.overloadedEmployees} overloaded` });

/** Capacity / allocated / actual / utilization rows for people or groups. */
export default function WorkloadTable({ rows, nameLabel = 'Employee', onRow }: { rows: Row[]; nameLabel?: string; onRow?: (key: Row['key']) => void }) {
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>{nameLabel}</TableCell>
          <TableCell align='right'>Capacity</TableCell>
          <TableCell align='right'>Allocated</TableCell>
          <TableCell align='right'>Actual</TableCell>
          <TableCell align='right'>Remaining</TableCell>
          <TableCell align='right'>Utilization</TableCell>
          <TableCell align='right'>Active alloc.</TableCell>
          <TableCell align='right'>Tasks</TableCell>
          <TableCell align='right'>Overdue</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key} hover={Boolean(onRow)} sx={{ cursor: onRow ? 'pointer' : 'default' }} onClick={() => onRow?.(r.key)}>
            <TableCell>
              {r.name}
              {r.sub && <Box component='span' sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}>{r.sub}</Box>}
            </TableCell>
            <TableCell align='right'>{fmtHours(r.capacityHours)}</TableCell>
            <TableCell align='right'>{fmtHours(r.allocatedHours)}</TableCell>
            <TableCell align='right'>{fmtHours(r.actualHours)}</TableCell>
            <TableCell align='right'>{fmtHours(r.remainingHours)}</TableCell>
            <TableCell align='right'><UtilBar value={r.utilization} status={r.workloadStatus} /></TableCell>
            <TableCell align='right'>{fmtHours(r.activeAllocationHours)}</TableCell>
            <TableCell align='right'>{r.activeTasks}</TableCell>
            <TableCell align='right' sx={{ color: r.overdueTasks ? 'error.main' : undefined }}>{r.overdueTasks}</TableCell>
            <TableCell><WorkloadChip status={r.workloadStatus} /></TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow><TableCell {...({ colSpan: 10 } as object)} align='center' sx={{ py: 3, color: 'text.secondary' }}>No one to show.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}
